import { useState, useEffect, useCallback } from 'react';
import { Loader2, ChevronRight, CheckCircle2, XCircle, Truck, Package, RotateCcw, CalendarPlus, Lock } from 'lucide-react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { DatePicker } from './ui/date-picker';
import { toast } from 'sonner';

const STATE_META = {
  booked:     { label: 'Booked',      icon: Package,       tone: 'text-slate-400'  },
  dispatched: { label: 'Dispatched',  icon: Truck,         tone: 'text-blue-400'    },
  on_hire:    { label: 'On Hire',     icon: RotateCcw,     tone: 'text-emerald-400' },
  off_hired:  { label: 'Off-Hired',   icon: Package,       tone: 'text-amber-400'   },
  closed:     { label: 'Closed',      icon: Lock,          tone: 'text-gray-500'    },
  cancelled:  { label: 'Cancelled',   icon: XCircle,       tone: 'text-red-500'     },
};

const ORDERED_STATES = ['booked', 'dispatched', 'on_hire', 'off_hired', 'closed'];

/**
 * Dry-hire lifecycle stepper + transition + extension UI.
 * Shown inside the Schedule job dialog for any dry-hire job that has been saved.
 */
export const HireLifecyclePanel = ({ jobId, api, onChange }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  // Extension dialog state
  const [extOpen, setExtOpen] = useState(false);
  const [extDate, setExtDate] = useState('');
  const [extReason, setExtReason] = useState('');

  // Transition dialog state
  const [txOpen, setTxOpen] = useState(false);
  const [txTarget, setTxTarget] = useState('');
  const [txNotes, setTxNotes] = useState('');

  const fetchData = useCallback(async () => {
    if (!jobId) return;
    try {
      setLoading(true);
      const res = await api.get(`/jobs/${jobId}/hire-lifecycle`);
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load lifecycle');
    } finally {
      setLoading(false);
    }
  }, [jobId, api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const submitTransition = async () => {
    try {
      setBusy(true);
      await api.post(`/jobs/${jobId}/hire-status`, {
        new_status: txTarget,
        notes: txNotes || null,
      });
      toast.success(`Moved to ${STATE_META[txTarget]?.label || txTarget}`);
      setTxOpen(false);
      setTxNotes('');
      await fetchData();
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Transition failed');
    } finally {
      setBusy(false);
    }
  };

  const submitExtension = async () => {
    if (!extDate) {
      toast.error('Pick a new end date');
      return;
    }
    try {
      setBusy(true);
      await api.post(`/jobs/${jobId}/extend-hire`, {
        new_end_date: extDate,
        reason: extReason || null,
      });
      toast.success('Hire extended');
      setExtOpen(false);
      setExtDate('');
      setExtReason('');
      await fetchData();
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Extension failed');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="mt-3 p-3 rounded-md border border-blue-500/30 bg-blue-500/5 text-xs text-muted-foreground flex items-center gap-2">
      <Loader2 className="h-3 w-3 animate-spin" />Loading lifecycle…
    </div>;
  }

  if (!data) return null;

  const current = data.hire_status;
  const isTerminal = data.is_terminal;
  const currentIdx = ORDERED_STATES.indexOf(current);
  const isCancelled = current === 'cancelled';
  const canExtend = ['booked', 'dispatched', 'on_hire'].includes(current);

  return (
    <div className="mt-3 p-3 rounded-md border border-blue-500/40 bg-blue-500/5 space-y-3" data-testid="hire-lifecycle-panel">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-semibold text-blue-400">Hire Lifecycle</h4>
          <p className="text-xs text-muted-foreground">
            {isCancelled ? 'Cancelled — no charges.' : isTerminal ? 'Closed — invoiced.' : 'Move through dispatch → on-hire → off-hire.'}
          </p>
        </div>
        {canExtend && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setExtOpen(true)}
            data-testid="extend-hire-btn"
          >
            <CalendarPlus className="h-3 w-3 mr-1" />
            Extend
          </Button>
        )}
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-1 overflow-x-auto pb-1">
        {ORDERED_STATES.map((state, i) => {
          const meta = STATE_META[state];
          const Icon = meta.icon;
          const done = !isCancelled && i < currentIdx;
          const active = !isCancelled && i === currentIdx;
          return (
            <div key={state} className="flex items-center gap-1 shrink-0">
              <div className={`flex items-center gap-1 px-2 py-1 rounded-md border text-[11px] ${
                done ? 'border-emerald-500/60 text-emerald-400 bg-emerald-500/5' :
                active ? 'border-blue-500 text-blue-400 bg-blue-500/10 font-semibold' :
                'border-gray-600 text-gray-500'
              }`}
                data-testid={`lifecycle-state-${state}`}
              >
                {done ? <CheckCircle2 className="h-3 w-3" /> : <Icon className="h-3 w-3" />}
                {meta.label}
              </div>
              {i < ORDERED_STATES.length - 1 && <ChevronRight className="h-3 w-3 text-gray-600" />}
            </div>
          );
        })}
        {isCancelled && (
          <div className="flex items-center gap-1 px-2 py-1 rounded-md border text-[11px] border-red-500/60 text-red-400 bg-red-500/5 ml-2">
            <XCircle className="h-3 w-3" />Cancelled
          </div>
        )}
      </div>

      {/* Action buttons for valid next states */}
      {!isTerminal && data.valid_next_states?.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {data.valid_next_states.map(target => {
            const meta = STATE_META[target];
            const Icon = meta.icon;
            const isDestructive = target === 'cancelled';
            return (
              <Button
                key={target}
                type="button"
                size="sm"
                variant={isDestructive ? 'ghost' : 'default'}
                className={isDestructive ? 'text-red-400 hover:bg-red-500/10' : 'btn-volt'}
                onClick={() => { setTxTarget(target); setTxOpen(true); }}
                data-testid={`lifecycle-transition-${target}`}
              >
                <Icon className="h-3 w-3 mr-1" />
                Move to {meta.label}
              </Button>
            );
          })}
        </div>
      )}

      {/* Extension history */}
      {data.hire_extensions?.length > 0 && (
        <div className="text-xs">
          <div className="text-muted-foreground mb-1">Extensions ({data.hire_extensions.length})</div>
          <div className="space-y-1">
            {data.hire_extensions.slice().reverse().map((ext, idx) => (
              <div key={idx} className="flex items-start justify-between gap-2 bg-background/40 rounded px-2 py-1">
                <div>
                  <span className="font-mono">{ext.from_end_date}</span>
                  <ChevronRight className="inline h-3 w-3 mx-0.5 text-muted-foreground" />
                  <span className="font-mono text-blue-400">{ext.to_end_date}</span>
                  {ext.reason && <span className="text-muted-foreground"> — {ext.reason}</span>}
                </div>
                <span className="text-[10px] text-muted-foreground">{ext.by_name}</span>
              </div>
            ))}
          </div>
          {data.original_end_date && (
            <div className="text-[10px] text-muted-foreground mt-1">
              Original end date: <span className="font-mono">{data.original_end_date}</span>
            </div>
          )}
        </div>
      )}

      {/* Transition confirm dialog */}
      <Dialog open={txOpen} onOpenChange={setTxOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Move to {STATE_META[txTarget]?.label}?</DialogTitle>
            <DialogDescription>
              {txTarget === 'off_hired' && 'Submitting this will stamp the off-hire date and create a draft post-hire inspection for the yard dispatcher.'}
              {txTarget === 'cancelled' && 'Cancelling a booking will stop all lifecycle tracking and skip invoicing.'}
              {['dispatched', 'on_hire', 'closed'].includes(txTarget) && 'This will advance the hire state and timestamp the transition.'}
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs">Notes (optional)</Label>
            <Textarea
              value={txNotes}
              onChange={e => setTxNotes(e.target.value)}
              placeholder="Add context for the audit log…"
              className="mt-1 text-sm"
              rows={2}
              data-testid="lifecycle-transition-notes"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTxOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={submitTransition} disabled={busy} data-testid="lifecycle-transition-confirm">
              {busy && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Extension dialog */}
      <Dialog open={extOpen} onOpenChange={setExtOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Extend Hire</DialogTitle>
            <DialogDescription>Push the end date forward. The original date is preserved for audit and the invoice preview will recompute automatically.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">New end date <span className="text-red-500">*</span></Label>
              <DatePicker
                value={extDate}
                onChange={(date) => setExtDate(date || '')}
                className="mt-1 w-full"
                testId="extend-hire-date"
              />
            </div>
            <div>
              <Label className="text-xs">Reason (optional)</Label>
              <Textarea
                value={extReason}
                onChange={e => setExtReason(e.target.value)}
                placeholder="Customer requested, weather delay, etc."
                rows={2}
                className="mt-1 text-sm"
                data-testid="extend-hire-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExtOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={submitExtension} disabled={busy} data-testid="extend-hire-confirm">
              {busy && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
              Extend
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HireLifecyclePanel;
